import { useEffect } from "react";
import { differenceInDays } from "date-fns";
import Drawer from "../../../../components/ui/Drawer/Drawer";
import DateRangePicker from "../../../../components/ui/DateRangePicker/DateRangePicker";
import GuestSelector from "../../../../components/ui/GuestSelector/GuestSelector";
import LoadingOverlay from "../../../../components/ui/LoadingOverlay/LoadingOverlay";
import SuccessState from "../../../../components/ui/SuccessState/SuccessState";
import ErrorState from "../../../../components/ui/ErrorState/ErrorState";
import { useBidForm } from "../../hooks/useBidForm";
import { useReservation } from "../../hooks/useReservation";
import styles from "./BidRequestDrawer.module.css";

/**
 * Componente BidRequestDrawer
 * Drawer principal para solicitar disponibilidad de noches
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Controla si el drawer está abierto
 * @param {function} props.onClose - Función para cerrar el drawer
 * @param {Object} props.offer - Datos de la oferta
 * @param {Object} props.user - Datos del usuario
 * @param {string} props.tipo - Tipo de oferta
 * @param {function} props.onSuccess - Callback cuando la solicitud es exitosa
 * @param {boolean} props.darkMode - Modo oscuro
 */
const BidRequestDrawer = ({
  isOpen,
  onClose,
  offer,
  user,
  tipo,
  onSuccess,
  darkMode = false,
}) => {
  const {
    formData,
    errors,
    setDateRange,
    setAdults,
    setChildren,
    setChildAge,
    validate,
    reset,
    getFormattedData,
  } = useBidForm();

  const {
    status,
    error: reservationError,
    submitReservation,
    reset: resetReservation,
    retry,
  } = useReservation();

  // Resetear formulario cuando se cierra el drawer
  useEffect(() => {
    if (!isOpen) {
      reset();
      resetReservation();
    }
  }, [isOpen, reset, resetReservation]);

  const handleSubmit = async () => {
    // Validar formulario
    if (!validate()) {
      return;
    }

    try {
      const formattedData = getFormattedData();
      await submitReservation({
        user,
        offer,
        formData: formattedData,
        tipo,
      });

      // Llamar callback de éxito si existe
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      // El error ya está manejado por useReservation
      console.error("Error al enviar solicitud:", err);
    }
  };

  const handleRetry = () => {
    retry({
      user,
      offer,
      formData: getFormattedData(),
      tipo,
    });
  };

  const handleContinue = () => {
    onClose();
    // Aquí podrías navegar a otra página si es necesario
  };

  const calculateNights = () => {
    if (formData.checkIn && formData.checkOut) {
      return differenceInDays(formData.checkOut, formData.checkIn);
    }
    return 0;
  };

  // Renderizar contenido según el estado
  const renderContent = () => {
    if (status === "loading") {
      return (
        <LoadingOverlay
          message="Estamos procesando tu solicitud..."
          subtitle="Estamos verificando la disponibilidad con el hotel. Por favor, no cierres esta ventana."
        />
      );
    }

    if (status === "success") {
      return (
        <SuccessState
          title="¡Solicitud recibida!"
          message="Un asesor se pondrá en contacto contigo pronto para confirmar la disponibilidad y ayudarte con tu reserva."
          reservation={{
            hotelName: offer?.titulo,
            thumbnail: offer?.portada,
            checkIn: formData.checkIn,
            checkOut: formData.checkOut,
            nights: calculateNights(),
          }}
          onContinue={handleContinue}
          darkMode={darkMode}
        />
      );
    }

    if (status === "error") {
      return (
        <ErrorState
          title="No pudimos completar tu solicitud"
          message={
            reservationError ||
            "Hubo un problema al procesar tu solicitud. Por favor, inténtalo de nuevo."
          }
          onRetry={handleRetry}
          onClose={onClose}
          darkMode={darkMode}
        />
      );
    }

    // Estado idle - Mostrar formulario
    return (
      <>
        <div
          className={`${styles["bid-drawer-content"]} ${
            darkMode ? styles.dark : ""
          }`}
        >
          {/* Selector de Fechas */}
          <DateRangePicker
            value={{ from: formData.checkIn, to: formData.checkOut }}
            onChange={setDateRange}
            error={errors.checkIn || errors.checkOut}
            darkMode={darkMode}
          />

          <div className={styles["bid-drawer-divider"]} />

          {/* Selector de Huéspedes */}
          <GuestSelector
            adults={formData.adults}
            children={formData.children}
            childrenAges={formData.childrenAges}
            onAdultsChange={setAdults}
            onChildrenChange={setChildren}
            onChildAgeChange={setChildAge}
            childrenAgesError={errors.childrenAges}
            darkMode={darkMode}
          />
        </div>
      </>
    );
  };

  // Footer solo se muestra en estado idle
  const renderFooter = () => {
    if (status !== "idle") return null;

    return (
      <div
        className={`${styles["bid-drawer-footer"]} ${
          darkMode ? styles.dark : ""
        }`}
      >
        <button
          type="button"
          className={`${styles["bid-drawer-button"]} ${styles["bid-drawer-button-cancel"]}`}
          onClick={onClose}
        >
          Cancelar
        </button>
        <button
          type="button"
          className={`${styles["bid-drawer-button"]} ${styles["bid-drawer-button-submit"]}`}
          onClick={handleSubmit}
          disabled={!formData.checkIn || !formData.checkOut}
        >
          Solicitar Reserva
        </button>
      </div>
    );
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={offer?.titulo || "Solicitar Noches"}
      subtitle="Paso 1: Solicitud de reserva"
      thumbnail={offer?.portada}
      footer={renderFooter()}
      darkMode={darkMode}
    >
      {renderContent()}
    </Drawer>
  );
};

export default BidRequestDrawer;
